import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../components/ui/command';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  ComposedChart,
  CartesianGrid, Tooltip as RTooltip, Legend, Cell,
} from 'recharts';
import {
  ArrowUp, ArrowDown, Minus, ChevronsUpDown, Check, RotateCcw,
  TrendingUp, Users, Rocket, Award, RefreshCw, Ban, Coins, Star, Info, Loader2,
  ArrowUpDown,
} from 'lucide-react';
import PeriodPresets, { PERIOD_PRESETS } from '../components/PeriodPresets';
import PMRankingInsights from '../components/PMRankingInsights';

const fmtCurrency = (v) => `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtNum = (v) => v == null ? '-' : Number(v).toLocaleString('it-IT');
const fmtPct = (v) => v == null ? '-' : `${Number(v).toFixed(1)}%`;

const KPI_TOOLTIPS = {
  projects_in_management: 'Progetti in fase "Partito" attivi almeno un giorno nel periodo, anche se scaduti nello stesso periodo.',
  projects_started: 'Progetti che nel periodo sono passati alla fase "Partito".',
  projects_outcome: 'Progetti con esito (perso/rinnovato/recesso) nel periodo.',
  renewals: 'Progetti con esito "rinnovato" nel periodo.',
  renewal_rate: 'Rinnovi / Progetti esitati nel periodo.',
  withdrawals: 'Progetti con esito "recesso" nel periodo.',
  withdrawal_rate: 'Recessi / Progetti esitati nel periodo.',
  success_fee_total: 'Somma delle success fee del periodo per progetti del PM filtrato.',
  success_fee_avg_per_active: 'Success Fee Totale / Progetti in gestione del periodo.',
  nps_avg: 'Media dell\'ultimo NPS disponibile per i progetti del PM entro fine periodo.',
};

function DeltaBadge({ value, isPercent, isInverse }) {
  if (value == null || value === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-400">
        <Minus className="w-3 h-3" /> 0
      </span>
    );
  }
  const positive = value > 0;
  const good = isInverse ? !positive : positive;
  const color = good ? 'text-emerald-600' : 'text-gray-500';
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${color}`}>
      {positive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
      {isPercent ? `${Math.abs(value).toFixed(1)}%` : Math.abs(value).toLocaleString('it-IT')}
    </span>
  );
}

function KPICard({ icon: Icon, label, value, suffix, tooltip, delta, isCurrency, isPercent, isInverse }) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-3">
        <div className="flex items-start justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <Icon className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</span>
            {tooltip && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3 h-3 text-gray-300 hover:text-gray-500 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                  {tooltip}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="flex items-end gap-2">
          <span className="text-xl font-bold text-gray-900">
            {value == null ? '-' : (isCurrency ? fmtCurrency(value) : (isPercent ? fmtPct(value) : fmtNum(value)))}
            {suffix && <span className="text-xs text-gray-400 ml-1">{suffix}</span>}
          </span>
          {delta && delta.delta_pct != null && (
            <DeltaBadge value={delta.delta_pct} isPercent isInverse={isInverse} />
          )}
        </div>
        {delta && delta.delta_abs != null && delta.delta_abs !== 0 && (
          <div className="text-[10px] text-gray-400 mt-0.5">
            {delta.delta_abs > 0 ? '+' : ''}{isCurrency ? fmtCurrency(delta.delta_abs) : fmtNum(delta.delta_abs)} vs periodo prec.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SortableHead({ label, sortKey, current, dir, onClick, align = 'left' }) {
  const active = current === sortKey;
  return (
    <TableHead
      className={`text-xs cursor-pointer select-none hover:bg-gray-100 transition-colors ${align === 'right' ? 'text-right' : ''} ${active ? 'text-orange-600 font-semibold' : ''}`}
      onClick={() => onClick(sortKey)}
      data-testid={`sort-${sortKey}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end w-full' : ''}`}>
        {label}
        {active ? (
          dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-30" />
        )}
      </span>
    </TableHead>
  );
}

function MultiSearchFilter({ label, values, onChange, options, testId }) {
  const [open, setOpen] = useState(false);
  const selectedNames = options.filter(o => values.includes(o.id)).map(o => o.name);
  const displayText = selectedNames.length === 0 ? 'Tutti' : selectedNames.length <= 2 ? selectedNames.join(', ') : `${selectedNames.length} selezionati`;
  const toggle = (id) => {
    onChange(values.includes(id) ? values.filter(v => v !== id) : [...values, id]);
  };
  return (
    <div className="space-y-1 min-w-[140px]">
      <Label className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className={`h-8 text-xs w-full justify-between font-normal px-3 ${values.length > 0 ? 'border-orange-300 bg-orange-50/50' : ''}`} data-testid={testId}>
            <span className="truncate">{displayText}</span>
            <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[240px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Cerca..." className="h-8 text-xs" />
            <CommandList>
              <CommandEmpty>Nessun risultato</CommandEmpty>
              <CommandGroup>
                {values.length > 0 && (
                  <CommandItem value="__clear__" onSelect={() => onChange([])}>
                    <RotateCcw className="mr-2 h-3 w-3 text-gray-400" />
                    <span className="text-gray-500">Deseleziona tutto</span>
                  </CommandItem>
                )}
                {options.map(o => (
                  <CommandItem key={o.id} value={o.name} onSelect={() => toggle(o.id)}>
                    <div className={`mr-2 h-3.5 w-3.5 rounded border flex items-center justify-center shrink-0 ${values.includes(o.id) ? 'bg-orange-500 border-orange-500' : 'border-gray-300'}`}>
                      {values.includes(o.id) && <Check className="h-2.5 w-2.5 text-white" />}
                    </div>
                    {o.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function PMSummaryDashboardPage() {
  // Filters
  const [periodPreset, setPeriodPreset] = useState('last_12_months');
  const [startDate, setStartDate] = useState(() => PERIOD_PRESETS.find(p => p.value === 'last_12_months').getRange().start);
  const [endDate, setEndDate] = useState(() => PERIOD_PRESETS.find(p => p.value === 'last_12_months').getRange().end);
  const [pmIds, setPmIds] = useState([]);
  const [buIds, setBuIds] = useState([]);
  const [clientIds, setClientIds] = useState([]);
  const [projectIds, setProjectIds] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [granularity, setGranularity] = useState('month');
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Sorting state for historical table
  const [sortKey, setSortKey] = useState(null);     // null => DB order (chronological)
  const [sortDir, setSortDir] = useState('asc');     // 'asc' | 'desc'
  
  const handleSort = (key) => {
    if (sortKey === key) {
      // cycle: asc -> desc -> none
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortKey(null); setSortDir('asc'); }
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  
  const handlePresetChange = (preset) => {
    setPeriodPreset(preset);
    if (preset === 'all') {
      setStartDate('');
      setEndDate('');
    } else if (preset !== 'custom') {
      const p = PERIOD_PRESETS.find(x => x.value === preset);
      if (p) {
        const r = p.getRange();
        setStartDate(r.start);
        setEndDate(r.end);
      }
    }
  };
  
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { granularity };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (pmIds.length) params.pm_id = pmIds.join(',');
      if (buIds.length) params.business_unit_id = buIds.join(',');
      if (clientIds.length) params.client_id = clientIds.join(',');
      if (projectIds.length) params.project_id = projectIds.join(',');
      if (statuses.length) params.project_status = statuses.join(',');
      const res = await api.get('/dashboard/pm-summary', { params });
      setData(res.data);
    } catch (err) {
      console.error('PM Summary load error:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, pmIds, buIds, clientIds, projectIds, statuses, granularity]);
  
  useEffect(() => { loadData(); }, [loadData]);
  
  const resetFilters = () => {
    setPmIds([]); setBuIds([]); setClientIds([]); setProjectIds([]); setStatuses([]);
  };
  const hasFilters = pmIds.length > 0 || buIds.length > 0 || clientIds.length > 0 || projectIds.length > 0 || statuses.length > 0;
  
  const meta = data?.filters_meta || {};
  const periodRows = data?.period_rows || [];
  const totals = data?.totals || {};
  const kpis = data?.selected_kpis || {};
  
  // SF type columns (union from all rows + totals)
  const sfTypeColumns = useMemo(() => {
    const set = new Set();
    periodRows.forEach(r => Object.keys(r.success_fee_by_type || {}).forEach(k => set.add(k)));
    Object.keys(totals.success_fee_by_type || {}).forEach(k => set.add(k));
    return Array.from(set).sort();
  }, [periodRows, totals]);
  
  // Sorted rows for table (preserves chronological default when sortKey is null)
  const sortedRows = useMemo(() => {
    if (!sortKey) return periodRows;
    const rows = [...periodRows];
    rows.sort((a, b) => {
      let av, bv;
      if (sortKey.startsWith('sft:')) {
        const t = sortKey.slice(4);
        av = (a.success_fee_by_type || {})[t] || 0;
        bv = (b.success_fee_by_type || {})[t] || 0;
      } else if (sortKey === 'label') {
        av = a.label; bv = b.label;
      } else {
        av = a[sortKey];
        bv = b[sortKey];
      }
      // null-safe compare (nulls last regardless of dir)
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return rows;
  }, [periodRows, sortKey, sortDir]);
  
  // Trend chart data
  const trendData = periodRows.map(r => ({
    label: r.label,
    Gestione: r.projects_in_management,
    Avviati: r.projects_started,
    Esitati: r.projects_outcome,
    Rinnovi: r.renewals,
    Recessi: r.withdrawals,
    SuccessFee: r.success_fee_total,
    NPS: r.nps_avg,
    FE_Esitati: r.fe_outcome || 0,
    FE_Vinti: r.fe_won || 0,
    FE_Persi: r.fe_lost || 0,
    FE_Volume: r.fe_generated_value || 0,
    FE_Conv: r.fe_conv_rate || 0,
  }));
  
  return (
    <TooltipProvider>
      <div className="space-y-5">
        {/* === FILTERS === */}
        <Card data-testid="pm-summary-filters">
          <CardContent className="py-3 px-4">
            <PeriodPresets
              activePreset={periodPreset}
              onPresetChange={handlePresetChange}
              showCustomDates={periodPreset === 'custom'}
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={(v) => { setStartDate(v); setPeriodPreset('custom'); }}
              onEndDateChange={(v) => { setEndDate(v); setPeriodPreset('custom'); }}
              testIdPrefix="pm-summary-period"
            />
            <div className="flex flex-wrap items-end gap-3 mt-3">
              <MultiSearchFilter label="Project Manager" values={pmIds} onChange={setPmIds}
                options={meta.project_managers || []} testId="pm-filter-pm" />
              <MultiSearchFilter label="Business Unit" values={buIds} onChange={setBuIds}
                options={meta.business_units || []} testId="pm-filter-bu" />
              <MultiSearchFilter label="Cliente" values={clientIds} onChange={setClientIds}
                options={meta.clients || []} testId="pm-filter-client" />
              <MultiSearchFilter label="Progetto" values={projectIds} onChange={setProjectIds}
                options={meta.projects || []} testId="pm-filter-project" />
              <MultiSearchFilter label="Stato Progetto" values={statuses} onChange={setStatuses}
                options={(meta.statuses || []).map(s => ({ id: s.value, name: s.label }))} testId="pm-filter-status" />
              <div className="space-y-1">
                <Label className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Vista</Label>
                <div className="flex gap-1 bg-gray-100 rounded p-0.5">
                  <button
                    onClick={() => setGranularity('month')}
                    className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all ${granularity === 'month' ? 'bg-white shadow text-orange-600' : 'text-gray-500'}`}
                    data-testid="pm-granularity-month"
                  >Mensile</button>
                  <button
                    onClick={() => setGranularity('quarter')}
                    className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all ${granularity === 'quarter' ? 'bg-white shadow text-orange-600' : 'text-gray-500'}`}
                    data-testid="pm-granularity-quarter"
                  >Trimestrale</button>
                </div>
              </div>
              {hasFilters && (
                <Button variant="ghost" size="sm" className="h-8 text-xs text-gray-500 gap-1" onClick={resetFilters} data-testid="pm-filter-reset">
                  <RotateCcw className="w-3 h-3" /> Reset
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        )}
        
        {!loading && (
          <>
            {/* === KPI CARDS === */}
            <div>
              <div className="text-xs text-gray-500 mb-2">
                Periodo corrente: <span className="font-medium text-gray-700">{kpis.period_label || '-'}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="pm-kpi-section">
                <KPICard icon={Users} label="Progetti in gestione" value={kpis.projects_in_management?.value} delta={kpis.projects_in_management} tooltip={KPI_TOOLTIPS.projects_in_management} />
                <KPICard icon={Rocket} label="Progetti avviati" value={kpis.projects_started?.value} delta={kpis.projects_started} tooltip={KPI_TOOLTIPS.projects_started} />
                <KPICard icon={Award} label="Progetti esitati" value={kpis.projects_outcome?.value} delta={kpis.projects_outcome} tooltip={KPI_TOOLTIPS.projects_outcome} />
                <KPICard icon={RefreshCw} label="Rinnovi" value={kpis.renewals?.value} delta={kpis.renewals} tooltip={KPI_TOOLTIPS.renewals} />
                <KPICard icon={Coins} label="Importo Rinnovi" value={kpis.renewals_amount?.value} delta={kpis.renewals_amount} isCurrency tooltip="Somma del valore dei prodotti contratto riconducibili ai rinnovi del periodo (selezionati al momento dell'esito)." />
                <KPICard icon={RefreshCw} label="% Rinnovi" value={kpis.renewal_rate?.value} delta={kpis.renewal_rate} isPercent tooltip={KPI_TOOLTIPS.renewal_rate} />
                <KPICard icon={Ban} label="Recessi" value={kpis.withdrawals?.value} delta={kpis.withdrawals} isInverse tooltip={KPI_TOOLTIPS.withdrawals} />
                <KPICard icon={Ban} label="% Recessi" value={kpis.withdrawal_rate?.value} delta={kpis.withdrawal_rate} isPercent isInverse tooltip={KPI_TOOLTIPS.withdrawal_rate} />
                <KPICard icon={Coins} label="Success Fee Totale" value={kpis.success_fee_total?.value} delta={kpis.success_fee_total} isCurrency tooltip={KPI_TOOLTIPS.success_fee_total} />
                <KPICard icon={TrendingUp} label="SF Media/Progetto" value={kpis.success_fee_avg_per_active?.value} delta={kpis.success_fee_avg_per_active} isCurrency tooltip={KPI_TOOLTIPS.success_fee_avg_per_active} />
                <KPICard icon={Star} label="NPS Medio" value={kpis.nps_avg?.value} delta={kpis.nps_avg} tooltip={KPI_TOOLTIPS.nps_avg} />
                <KPICard icon={TrendingUp} label="Up-Sell" value={kpis.upsells_count?.value} delta={kpis.upsells_count} tooltip="Numero contratti up-sell firmati nel periodo (per data firma). Conteggio per contratti distinti." data-testid="kpi-upsells-count" />
                <KPICard icon={Coins} label="Importo Up-Sell" value={kpis.upsells_amount?.value} delta={kpis.upsells_amount} isCurrency tooltip="Importo totale dei prodotti up-sell approvati firmati nel periodo." data-testid="kpi-upsells-amount" />
              </div>
            </div>
            
            {/* === TREND CHARTS === */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Trend Progetti</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RTooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="Gestione" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="Avviati" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="Esitati" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Trend Rinnovi e Recessi</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RTooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="Rinnovi" fill="#10b981" />
                      <Bar dataKey="Recessi" fill="#ef4444" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Trend Success Fee Totale</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                      <RTooltip formatter={(v) => fmtCurrency(v)} />
                      <Bar dataKey="SuccessFee" fill="#f97316">
                        {trendData.map((_, i) => <Cell key={i} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Trend NPS Medio</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} domain={[0, 10]} />
                      <RTooltip />
                      <Line type="monotone" dataKey="NPS" stroke="#a855f7" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* === FRONT-END CHARTS === */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Front-End Esitati nel Tempo</CardTitle>
                  <p className="text-xs text-gray-500">Linee: esitati / vinti / persi. Barra: volume € generato dai vinti.</p>
                </CardHeader>
                <CardContent className="pt-0">
                  <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={trendData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `€${(v/1000).toFixed(0)}k` : `€${v}`} />
                      <RTooltip formatter={(v, name) => name === 'Volume €' ? [fmtCurrency(v), name] : [fmtNum(v), name]} />
                      <Legend wrapperStyle={{ fontSize: '11px' }} />
                      <Bar yAxisId="right" dataKey="FE_Volume" name="Volume €" fill="#c084fc" opacity={0.55} radius={[4, 4, 0, 0]} />
                      <Line yAxisId="left" type="monotone" dataKey="FE_Esitati" name="Esitati" stroke="#6b7280" strokeWidth={2} dot={{ r: 3 }} />
                      <Line yAxisId="left" type="monotone" dataKey="FE_Vinti" name="Vinti" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                      <Line yAxisId="left" type="monotone" dataKey="FE_Persi" name="Persi" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Tasso Conversione Front-End nel Tempo</CardTitle>
                  <p className="text-xs text-gray-500">Vinti / Esitati (%)</p>
                </CardHeader>
                <CardContent className="pt-0">
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={trendData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                      <RTooltip formatter={(v) => `${v}%`} />
                      <Bar dataKey="FE_Conv" name="Conversione" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
            
            {/* === HISTORICAL TABLE === */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Tabella Storica</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 overflow-x-auto">
                <Table data-testid="pm-summary-table">
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <SortableHead label={granularity === 'month' ? 'Mese' : 'Trim.'} sortKey="label" current={sortKey} dir={sortDir} onClick={handleSort} />
                      <SortableHead label="In gestione" sortKey="projects_in_management" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      <SortableHead label="Avviati" sortKey="projects_started" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      <SortableHead label="Esitati" sortKey="projects_outcome" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      <SortableHead label="Rinnovi" sortKey="renewals" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      <SortableHead label="Importo Rinn." sortKey="renewals_amount" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      <SortableHead label="% Rinn." sortKey="renewal_rate" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      <SortableHead label="Recessi" sortKey="withdrawals" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      <SortableHead label="% Rec." sortKey="withdrawal_rate" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      <SortableHead label="SF Totale" sortKey="success_fee_total" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      {sfTypeColumns.map(t => (
                        <SortableHead key={t} label={t} sortKey={`sft:${t}`} current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      ))}
                      <SortableHead label="SF Media/Att." sortKey="success_fee_avg_per_active" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      <SortableHead label="Up-Sell" sortKey="upsells_count" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      <SortableHead label="Imp. Up-Sell" sortKey="upsells_amount" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      <SortableHead label="FE Esitati" sortKey="fe_outcome" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      <SortableHead label="FE Vinti" sortKey="fe_won" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      <SortableHead label="% Conv FE" sortKey="fe_conv_rate" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      <SortableHead label="Post-FE €" sortKey="fe_generated_value" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                      <SortableHead label="NPS" sortKey="nps_avg" current={sortKey} dir={sortDir} onClick={handleSort} align="right" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedRows.map(r => (
                      <TableRow key={r.key} className="hover:bg-orange-50/30" data-testid={`pm-row-${r.key}`}>
                        <TableCell className="text-xs font-medium">{r.label}</TableCell>
                        <TableCell className="text-xs text-right">{fmtNum(r.projects_in_management)}</TableCell>
                        <TableCell className="text-xs text-right">{fmtNum(r.projects_started)}</TableCell>
                        <TableCell className="text-xs text-right">{fmtNum(r.projects_outcome)}</TableCell>
                        <TableCell className="text-xs text-right text-emerald-600">{fmtNum(r.renewals)}</TableCell>
                        <TableCell className="text-xs text-right text-emerald-600">{fmtCurrency(r.renewals_amount || 0)}</TableCell>
                        <TableCell className="text-xs text-right text-gray-500">{fmtPct(r.renewal_rate)}</TableCell>
                        <TableCell className="text-xs text-right text-red-500">{fmtNum(r.withdrawals)}</TableCell>
                        <TableCell className="text-xs text-right text-gray-500">{fmtPct(r.withdrawal_rate)}</TableCell>
                        <TableCell className="text-xs text-right font-medium">{fmtCurrency(r.success_fee_total)}</TableCell>
                        {sfTypeColumns.map(t => (
                          <TableCell key={t} className="text-xs text-right text-gray-600">
                            {fmtCurrency((r.success_fee_by_type || {})[t] || 0)}
                          </TableCell>
                        ))}
                        <TableCell className="text-xs text-right">{fmtCurrency(r.success_fee_avg_per_active)}</TableCell>
                        <TableCell className="text-xs text-right text-orange-600 font-medium">{fmtNum(r.upsells_count)}</TableCell>
                        <TableCell className="text-xs text-right text-orange-600">{fmtCurrency(r.upsells_amount || 0)}</TableCell>
                        <TableCell className="text-xs text-right text-purple-600 font-medium">{fmtNum(r.fe_outcome)}</TableCell>
                        <TableCell className="text-xs text-right text-purple-600">{fmtNum(r.fe_won)}</TableCell>
                        <TableCell className="text-xs text-right text-purple-700 font-medium">{r.fe_conv_rate != null ? `${r.fe_conv_rate}%` : '-'}</TableCell>
                        <TableCell className="text-xs text-right text-purple-700">{fmtCurrency(r.fe_generated_value || 0)}</TableCell>
                        <TableCell className="text-xs text-right">{r.nps_avg != null ? r.nps_avg.toFixed(1) : '-'}</TableCell>
                      </TableRow>
                    ))}
                    {/* TOTALS ROW */}
                    <TableRow className="bg-orange-50/60 font-semibold border-t-2 border-orange-200" data-testid="pm-row-totals">
                      <TableCell className="text-xs">{totals.label || 'Totale'}</TableCell>
                      <TableCell className="text-xs text-right">{fmtNum(totals.projects_in_management)}</TableCell>
                      <TableCell className="text-xs text-right">{fmtNum(totals.projects_started)}</TableCell>
                      <TableCell className="text-xs text-right">{fmtNum(totals.projects_outcome)}</TableCell>
                      <TableCell className="text-xs text-right text-emerald-700">{fmtNum(totals.renewals)}</TableCell>
                      <TableCell className="text-xs text-right text-emerald-700">{fmtCurrency(totals.renewals_amount || 0)}</TableCell>
                      <TableCell className="text-xs text-right">{fmtPct(totals.renewal_rate)}</TableCell>
                      <TableCell className="text-xs text-right text-red-700">{fmtNum(totals.withdrawals)}</TableCell>
                      <TableCell className="text-xs text-right">{fmtPct(totals.withdrawal_rate)}</TableCell>
                      <TableCell className="text-xs text-right">{fmtCurrency(totals.success_fee_total)}</TableCell>
                      {sfTypeColumns.map(t => (
                        <TableCell key={t} className="text-xs text-right">
                          {fmtCurrency((totals.success_fee_by_type || {})[t] || 0)}
                        </TableCell>
                      ))}
                      <TableCell className="text-xs text-right">{fmtCurrency(totals.success_fee_avg_per_active)}</TableCell>
                      <TableCell className="text-xs text-right text-orange-700 font-semibold">{fmtNum(totals.upsells_count)}</TableCell>
                      <TableCell className="text-xs text-right text-orange-700">{fmtCurrency(totals.upsells_amount || 0)}</TableCell>
                      <TableCell className="text-xs text-right text-purple-700 font-semibold">{fmtNum(totals.fe_outcome)}</TableCell>
                      <TableCell className="text-xs text-right text-purple-700 font-semibold">{fmtNum(totals.fe_won)}</TableCell>
                      <TableCell className="text-xs text-right text-purple-700 font-semibold">{totals.fe_conv_rate != null ? `${totals.fe_conv_rate}%` : '-'}</TableCell>
                      <TableCell className="text-xs text-right text-purple-700 font-semibold">{fmtCurrency(totals.fe_generated_value || 0)}</TableCell>
                      <TableCell className="text-xs text-right">{totals.nps_avg != null ? totals.nps_avg.toFixed(1) : '-'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                {periodRows.length === 0 && (
                  <p className="text-center text-sm text-gray-500 py-8">Nessun dato per il periodo selezionato.</p>
                )}
              </CardContent>
            </Card>
            
            {/* === RANKING & INSIGHTS === */}
            <PMRankingInsights filters={{
              start_date: startDate || undefined,
              end_date: endDate || undefined,
              pm_id: pmIds.length ? pmIds.join(',') : undefined,
              business_unit_id: buIds.length ? buIds.join(',') : undefined,
              client_id: clientIds.length ? clientIds.join(',') : undefined,
              project_id: projectIds.length ? projectIds.join(',') : undefined,
            }} />
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
